import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { differenceInDays } from 'date-fns';

export interface DealWithAging {
  id: string;
  contact_id: string | null;
  stage: string;
  created_at: string;
  total_amount: number | null;
  contact?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
  };
  sales_consultant_user?: {
    id: string;
    display_name: string | null;
    first_name: string | null;
    last_name: string | null;
  };
  sales_development_user?: {
    id: string;
    display_name: string | null;
    first_name: string | null;
    last_name: string | null;
  };
  last_contact_date: Date;
  days_since_contact: number;
  bucket: AgingBucket;
}

export type AgingBucket = '30+' | '15-30' | '8-14' | '4-7' | '2-3' | '0-1';

export interface BucketConfig {
  key: AgingBucket;
  label: string;
  min: number;
  max: number;
  color: string;
  bgColor: string;
}

// Ordered from most critical to least critical
export const AGING_BUCKETS: BucketConfig[] = [
  { key: '30+', label: '30+ dias', min: 31, max: Infinity, color: 'hsl(0, 70%, 40%)', bgColor: 'hsl(0, 70%, 95%)' },
  { key: '15-30', label: '15-30 dias', min: 15, max: 30, color: 'hsl(0, 70%, 55%)', bgColor: 'hsl(0, 70%, 95%)' },
  { key: '8-14', label: '8-14 dias', min: 8, max: 14, color: 'hsl(30, 90%, 50%)', bgColor: 'hsl(30, 90%, 95%)' },
  { key: '4-7', label: '4-7 dias', min: 4, max: 7, color: 'hsl(45, 90%, 45%)', bgColor: 'hsl(45, 90%, 95%)' },
  { key: '2-3', label: '2-3 dias', min: 2, max: 3, color: 'hsl(55, 85%, 45%)', bgColor: 'hsl(55, 85%, 95%)' },
  { key: '0-1', label: '0-1 dias', min: 0, max: 1, color: 'hsl(140, 70%, 40%)', bgColor: 'hsl(140, 70%, 95%)' },
];

function getBucket(days: number): AgingBucket {
  if (days >= 31) return '30+';
  if (days >= 15) return '15-30';
  if (days >= 8) return '8-14';
  if (days >= 4) return '4-7';
  if (days >= 2) return '2-3';
  return '0-1';
}

export function useDealAging(deals: any[]) {
  const [lastContactMap, setLastContactMap] = useState<Map<string, Date>>(new Map());
  const [loading, setLoading] = useState(true);

  // Get unique contact IDs from active deals
  const activeDeals = useMemo(() => {
    return deals.filter(d => {
      const stage = (d.stage ?? '').toLowerCase();
      return stage !== 'won' && stage !== 'lost';
    });
  }, [deals]);

  const contactIds = useMemo(() => {
    const ids = new Set<string>();
    activeDeals.forEach(d => {
      if (d.contact_id) ids.add(d.contact_id);
    });
    return Array.from(ids);
  }, [activeDeals]);

  // Fetch last contact dates from calls and messages
  useEffect(() => {
    if (contactIds.length === 0) {
      setLastContactMap(new Map());
      setLoading(false);
      return;
    }

    const fetchLastContacts = async () => {
      setLoading(true);
      const contactMap = new Map<string, Date>();

      try {
        // Fetch last call per contact (only completed calls)
        const { data: calls } = await supabase
          .from('calls')
          .select('contact_id, created_at')
          .in('contact_id', contactIds)
          .eq('status', 'completed')
          .order('created_at', { ascending: false });

        // Group by contact and get the most recent
        const callsByContact = new Map<string, Date>();
        calls?.forEach(call => {
          if (call.contact_id && !callsByContact.has(call.contact_id)) {
            callsByContact.set(call.contact_id, new Date(call.created_at));
          }
        });

        // Fetch last message per contact
        const { data: messages } = await supabase
          .from('messages')
          .select('contact_id, created_at')
          .in('contact_id', contactIds)
          .order('created_at', { ascending: false });

        // Group by contact and get the most recent
        const messagesByContact = new Map<string, Date>();
        messages?.forEach(msg => {
          if (msg.contact_id && !messagesByContact.has(msg.contact_id)) {
            messagesByContact.set(msg.contact_id, new Date(msg.created_at));
          }
        });

        // Merge: take the most recent between call and message
        contactIds.forEach(contactId => {
          const lastCall = callsByContact.get(contactId);
          const lastMessage = messagesByContact.get(contactId);
          
          let lastContact: Date | null = null;
          if (lastCall && lastMessage) {
            lastContact = lastCall > lastMessage ? lastCall : lastMessage;
          } else {
            lastContact = lastCall || lastMessage || null;
          }

          if (lastContact) {
            contactMap.set(contactId, lastContact);
          }
        });

        setLastContactMap(contactMap);
      } catch (error) {
        console.error('Error fetching last contacts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLastContacts();
  }, [contactIds]);

  // Enrich deals with aging data
  const dealsWithAging: DealWithAging[] = useMemo(() => {
    const now = new Date();
    
    return activeDeals.map(deal => {
      const lastContact = deal.contact_id 
        ? lastContactMap.get(deal.contact_id) || new Date(deal.created_at)
        : new Date(deal.created_at);
      
      const daysSince = differenceInDays(now, lastContact);
      
      return {
        ...deal,
        last_contact_date: lastContact,
        days_since_contact: daysSince,
        bucket: getBucket(daysSince),
      };
    }).sort((a, b) => b.days_since_contact - a.days_since_contact); // Most critical first
  }, [activeDeals, lastContactMap]);

  // Group by bucket for chart
  const bucketData = useMemo(() => {
    const stageOrder = ['new', 'contact', 'scheduled', 'proposal', 'negotiation'];
    
    return AGING_BUCKETS.map(bucket => {
      const bucketDeals = dealsWithAging.filter(d => d.bucket === bucket.key);
      
      // Count by stage
      const stageCounts: Record<string, number> = {};
      stageOrder.forEach(stage => stageCounts[stage] = 0);
      
      bucketDeals.forEach(deal => {
        const stage = (deal.stage ?? '').toLowerCase();
        if (stageCounts.hasOwnProperty(stage)) {
          stageCounts[stage]++;
        }
      });

      return {
        ...bucket,
        total: bucketDeals.length,
        deals: bucketDeals,
        stageCounts,
      };
    });
  }, [dealsWithAging]);

  return {
    dealsWithAging,
    bucketData,
    loading,
  };
}
